import React, { useState } from 'react';
import { ConfirmModal } from '../../../shared/components/ConfirmModal';
import type { AdminCompanyStatus } from '../../../models/adminCompany';

export interface CompanyStatusPanelProps {
  companyName: string;
  status: string;
  /** Applies the requested status. Should reject (throw) on failure so the modal can surface it. */
  onConfirm: (next: AdminCompanyStatus) => Promise<void>;
}

/**
 * Renders the current company status with a suspend/reactivate action gated behind a confirmation
 * modal. Cancelled companies are read-only (no admin transition allowed).
 */
export function CompanyStatusPanel({ companyName, status, onConfirm }: CompanyStatusPanelProps): React.ReactElement {
  const [modalOpen, setModalOpen] = useState(false);
  const normalized = status.toLowerCase();
  const isActive = normalized === 'active';
  const isCancelled = normalized === 'cancelled';
  const next: AdminCompanyStatus = isActive ? 'suspended' : 'active';

  const actionLabel = isActive ? 'Suspend company' : 'Reactivate company';
  const confirmVariant = isActive ? 'danger' : 'primary';
  const modalMessage = isActive ? (
    <p style={{ marginTop: 0 }}>
      Suspending <strong>{companyName}</strong> immediately blocks the company (and its merchants) from
      accessing the platform until it is reactivated. Continue?
    </p>
  ) : (
    <p style={{ marginTop: 0 }}>
      Reactivating <strong>{companyName}</strong> restores platform access for the company and its
      merchants. Continue?
    </p>
  );

  return (
    <div className="bo-card">
      <div className="bo-admin-table-head">
        <h2 className="bo-admin-section-title">Access control</h2>
        <p className="bo-muted">Suspend to block platform access, or reactivate to restore it.</p>
      </div>
      <div className="bo-actions" style={{ alignItems: 'center', gap: '0.75rem' }}>
        <span className={`bo-status-pill ${isActive ? 'bo-status-active' : 'bo-status-inactive'}`}>{status}</span>
        {isCancelled ? (
          <span className="bo-muted">Cancelled companies cannot be changed from the admin portal.</span>
        ) : (
          <button
            type="button"
            className={isActive ? 'bo-btn bo-btn-danger' : 'bo-btn bo-btn-primary'}
            onClick={() => setModalOpen(true)}
          >
            {actionLabel}
          </button>
        )}
      </div>

      <ConfirmModal
        visible={modalOpen}
        title={actionLabel}
        message={modalMessage}
        confirmLabel={isActive ? 'Suspend' : 'Reactivate'}
        pendingLabel={isActive ? 'Suspending…' : 'Reactivating…'}
        confirmVariant={confirmVariant}
        onClose={() => setModalOpen(false)}
        onConfirm={() => onConfirm(next)}
      />
    </div>
  );
}
